import type { CalendarDate } from '@internationalized/date';

import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { billingHistoryControllerFindAllForTeam } from '@/api/core-api';
import { Table, TableCard } from '@/components/application/table/table';
import { Button, ExternalLinkButton } from '@/components/base/buttons/button';
import { DatePicker } from '@/components/base/date-picker/date-picker';
import { BASE_PATH } from '@/constants/config';
import { formatChargeCents } from '@/utils/currency';
import { getClient } from '@/utils/get-api-client';

import { billingDateRange } from './-billing-date-range';
import { downloadReceipt } from './-receipt-download';

export const Route = createFileRoute(
  '/researchers/_authenticated/billing/history',
)({
  component: BillingHistoryPage,
  head: () => ({ meta: [{ title: t`Billing history` }] }),
});

const PAGE_SIZE = 10;

function BillingHistoryPage() {
  const { user, activeDashboard, env, hasLegacyPricing } =
    Route.useRouteContext();
  const teamId = activeDashboard.team ?? '';
  const orgId = activeDashboard.organization ?? '';
  const classicBase = `${env.WEBSITE_URL}/dashboard/organizations/${orgId}/teams/${teamId}/billing`;
  const [from, setFrom] = useState<CalendarDate | null>(null);
  const [to, setTo] = useState<CalendarDate | null>(null);
  const [page, setPage] = useState(1);
  const [downloadingReceipt, setDownloadingReceipt] = useState<string | null>(
    null,
  );
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const receipt = async (chargeId: string) => {
    setDownloadingReceipt(chargeId);
    setReceiptError(null);
    try {
      await downloadReceipt(
        `${BASE_PATH}/v4/pricing/teams/${teamId}/charges/${chargeId}/receipt`,
      );
    } catch {
      setReceiptError(
        t`Could not download this receipt. Please try again later or contact support.`,
      );
    } finally {
      setDownloadingReceipt(null);
    }
  };
  const dateRange = billingDateRange(from, to);
  const { data, isPending, isError } = useQuery({
    queryKey: [
      'billing-history',
      user.id,
      teamId,
      page,
      dateRange.fromDate,
      dateRange.toDate,
    ],
    enabled: !!teamId && (!from || !to || from.compare(to) <= 0),
    queryFn: async () => {
      const response = await billingHistoryControllerFindAllForTeam({
        client: getClient(user.id),
        path: { teamId },
        query: { page, pageSize: PAGE_SIZE, includeCount: true, ...dateRange },
        throwOnError: true,
      });
      return response.data;
    },
  });
  const totalPages = Math.max(
    1,
    Math.ceil((data?.totalResults ?? 0) / PAGE_SIZE),
  );

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-display-xs font-semibold text-primary">
          <Trans>Billing history</Trans>
        </h1>
        {hasLegacyPricing && (
          <div className="flex flex-wrap gap-2">
            <ExternalLinkButton
              href={`${classicBase}/lts/transactions`}
              target="_blank"
              size="sm"
              color="secondary"
            >
              <Trans>Legacy credit purchases</Trans>
            </ExternalLinkButton>
            <ExternalLinkButton
              href={`${classicBase}/lts/payments`}
              target="_blank"
              size="sm"
              color="secondary"
            >
              <Trans>Legacy card payments</Trans>
            </ExternalLinkButton>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-48">
          <DatePicker
            label={t`From`}
            size="sm"
            value={from}
            onChange={(date) => {
              setFrom(date);
              setPage(1);
            }}
          />
        </div>
        <div className="w-full sm:w-48">
          <DatePicker
            label={t`To`}
            size="sm"
            value={to}
            onChange={(date) => {
              setTo(date);
              setPage(1);
            }}
          />
        </div>
      </div>
      {from && to && from.compare(to) > 0 && (
        <p role="alert" className="text-sm text-error-primary">
          <Trans>The end date must be after the start date.</Trans>
        </p>
      )}
      {(!from || !to || from.compare(to) <= 0) && isPending && (
        <p role="status" className="text-sm text-secondary">
          <Trans>Loading billing history…</Trans>
        </p>
      )}
      {isError && (
        <p role="alert" className="text-sm text-error-primary">
          <Trans>Could not load billing history.</Trans>
        </p>
      )}
      {receiptError && (
        <p role="alert" className="text-sm text-error-primary">
          {receiptError}
        </p>
      )}
      {(!from || !to || from.compare(to) <= 0) &&
        data &&
        data.results.length === 0 && (
          <p className="text-sm text-secondary">
            {from || to ? t`No results` : t`No history yet`}
          </p>
        )}
      {data && data.results.length > 0 && (
        <TableCard.Root className="overflow-x-auto">
          <Table aria-label={t`Team billing history`} selectionMode="none">
            <Table.Header>
              <Table.Head id="date" label={t`Date`} isRowHeader />
              <Table.Head id="amount" label={t`Amount`} />
              <Table.Head id="reason" label={t`Charge reason`} />
              <Table.Head id="receipt" aria-label={t`Receipt`} />
            </Table.Header>
            <Table.Body>
              {data.results.map((charge) => (
                <Table.Row key={charge.id} id={charge.id}>
                  <Table.Cell>
                    {new Date(charge.createdAt).toLocaleDateString()}
                  </Table.Cell>
                  <Table.Cell>{formatChargeCents(charge.charge)}</Table.Cell>
                  <Table.Cell>{charge.description ?? '—'}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="sm"
                      color="link-color"
                      isLoading={downloadingReceipt === charge.id}
                      isDisabled={downloadingReceipt !== null}
                      onClick={() => void receipt(charge.id)}
                    >
                      <Trans>Download</Trans>
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </TableCard.Root>
      )}
      {data && totalPages > 1 && (
        <div className="flex items-center justify-end gap-3 text-sm">
          <Button
            size="sm"
            color="secondary"
            isDisabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            <Trans>Previous</Trans>
          </Button>
          <span>
            {page} / {totalPages}
          </span>
          <Button
            size="sm"
            color="secondary"
            isDisabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            <Trans>Next</Trans>
          </Button>
        </div>
      )}
    </>
  );
}
